import axios from 'axios';

// Module-level cache of in-flight GETs, keyed by URL. Coalesces identical
// concurrent requests fired by sibling components (e.g. Banner + TestSchedule
// both loading the same calendar schedule on Dashboard mount).
const inFlightGets = new Map();

export function dedupedGet(url) {
  if (inFlightGets.has(url)) return inFlightGets.get(url);

  const request = axios.get(url).finally(() => {
    inFlightGets.delete(url);
  });

  inFlightGets.set(url, request);
  return request;
}

// Module-level cache of resolved GETs, keyed by URL. Unlike dedupedGet, the
// entry is kept after the request resolves, so a component that unmounts and
// remounts (e.g. switching analysis tabs) gets the cached response instead of
// firing the request again. Lives for the JS session (cleared on page reload).
const resolvedGets = new Map();

export async function cachedGet(url) {
  if (!resolvedGets.has(url)) {
    const request = axios.get(url).catch((error) => {
      resolvedGets.delete(url);
      throw error;
    });
    resolvedGets.set(url, request);
  }

  // Consumers routinely mutate response.data in place (e.g. `delete data.subjectMeta`).
  // Hand back a clone so that never corrupts the cached copy for the next caller.
  const response = await resolvedGets.get(url);
  return { ...response, data: structuredClone(response.data) };
}

// Evicts every cachedGet entry whose URL starts with `prefix`. Callers should
// invoke this on unmount of the screen that owns those URLs (e.g. TestAnalysis
// clearing `/api/test/analysis/v2/${packId}/${testId}` on navigating away) so
// the cache doesn't outlive the screen or grow unbounded across a session.
export function clearCachedGet(prefix) {
  for (const url of resolvedGets.keys()) {
    if (url.startsWith(prefix)) resolvedGets.delete(url);
  }
}
